import { type FormEvent, useCallback, useEffect, useState } from 'react';
import { getJson, mutateJson } from '../api/client';
import { Field, Input, Select } from '../components/form';
import { Alert, Badge, Button, Card, EmptyState, Spinner } from '../components/ui';
import { mesAno } from '../lib/format';
import { navigate } from '../router';
import styles from './screens.module.css';

interface Competencia {
  id: number;
  ano: number;
  mes: number;
}
interface Passo {
  id: string;
  numero: number;
  concluido: boolean;
  aplicavel: boolean;
  bloqueado: boolean;
}
interface Status {
  passos: Passo[];
  concluidos: number;
  total: number;
  recebimentos: { pagaram: number; total: number };
}

type Carga =
  | { estado: 'carregando' }
  | { estado: 'erro' }
  | { estado: 'ok'; atual: Competencia | null };
type CargaStatus = { estado: 'carregando' } | { estado: 'erro' } | { estado: 'ok'; status: Status };
type Campo = 'mes' | 'ano' | 'vencimento' | 'reserva';

/** Rótulo e tela de cada passo (docs/08 §8.2). `rota` ausente = tela ainda não entregue. */
const PASSOS: Record<string, { rotulo: string; rota?: string }> = {
  previsao: { rotulo: 'Previsão do mês', rota: '/previsao' },
  gas: { rotulo: 'Leitura de gás', rota: '/gas' },
  faturas: { rotulo: 'Gerar faturas', rota: '/faturas-do-mes' },
  extrato: { rotulo: 'Conferir extrato', rota: '/conferencia' },
  balancete: { rotulo: 'Publicar balancete', rota: '/balancete-do-mes' },
};

const MESES = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

/** Erros de validação da API (`POST /api/competencias`) que pertencem a um campo. */
const ERRO_DE_CAMPO: Record<string, { campo: Campo; mensagem: string }> = {
  competencia_duplicada: { campo: 'mes', mensagem: 'Já existe uma competência para esse mês.' },
  invalid_mes: { campo: 'mes', mensagem: 'Escolha o mês.' },
  invalid_ano: { campo: 'ano', mensagem: 'Informe um ano entre 2000 e 2100.' },
  invalid_vencimento: { campo: 'vencimento', mensagem: 'Informe o vencimento.' },
  invalid_reserva: { campo: 'reserva', mensagem: 'Informe um percentual de 0 a 100.' },
};
const ERRO_GERAL: Record<string, string> = {
  tenant_suspended: 'Condomínio suspenso — somente leitura.',
};
const ERRO_GERAL_PADRAO = 'Não foi possível abrir a competência. Tente novamente.';

/**
 * Checklist de fechamento do mês na Home do síndico (docs/08 §8.2, #131): competência
 * atual, progresso e os 5 passos, calculados no servidor. `editavel` só para condomínio
 * ativo (abrir competência é POST do síndico; suspenso é somente leitura).
 */
export function Fechamento({ editavel }: { editavel: boolean }) {
  const [carga, setCarga] = useState<Carga>({ estado: 'carregando' });
  const [abrindo, setAbrindo] = useState(false);

  const carregar = useCallback(async () => {
    // A API ordena da mais recente para a mais antiga: a atual é a primeira.
    const dados = await getJson<{ competencias?: Competencia[] }>('/api/competencias');
    setCarga(dados ? { estado: 'ok', atual: dados.competencias?.[0] ?? null } : { estado: 'erro' });
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  if (carga.estado === 'carregando') return <Spinner />;
  if (carga.estado === 'erro') {
    return <Alert kind="danger">Não foi possível carregar as competências.</Alert>;
  }

  const aoAbrir = async () => {
    setAbrindo(false);
    setCarga({ estado: 'carregando' });
    await carregar();
  };

  if (carga.atual === null) {
    return editavel ? (
      <AberturaForm onAberta={aoAbrir} />
    ) : (
      <EmptyState>Nenhuma competência aberta.</EmptyState>
    );
  }
  return (
    <div className={styles.stack}>
      <Checklist competencia={carga.atual} />
      {editavel ? (
        abrindo ? (
          <AberturaForm onAberta={aoAbrir} />
        ) : (
          <div>
            <Button variant="secondary" onClick={() => setAbrindo(true)}>
              Abrir novo mês
            </Button>
          </div>
        )
      ) : null}
    </div>
  );
}

/** Progresso, passos e recebimentos da competência. */
function Checklist({ competencia }: { competencia: Competencia }) {
  const [carga, setCarga] = useState<CargaStatus>({ estado: 'carregando' });

  useEffect(() => {
    // Descarta a resposta de uma competência que já não é a atual.
    let vivo = true;
    setCarga({ estado: 'carregando' });
    void getJson<Status>(
      `/api/competencias/${encodeURIComponent(competencia.id)}/fechamento-status`,
    ).then((status) => {
      if (vivo) setCarga(status ? { estado: 'ok', status } : { estado: 'erro' });
    });
    return () => {
      vivo = false;
    };
  }, [competencia.id]);

  return (
    <Card title={`Competência ${mesAno(competencia)}`}>
      {carga.estado === 'carregando' ? <Spinner /> : null}
      {carga.estado === 'erro' ? (
        <Alert kind="danger">Não foi possível carregar o fechamento do mês.</Alert>
      ) : null}
      {carga.estado === 'ok' ? (
        <StatusFechamento status={carga.status} competenciaId={competencia.id} />
      ) : null}
    </Card>
  );
}

function StatusFechamento({ status, competenciaId }: { status: Status; competenciaId: number }) {
  const { pagaram, total } = status.recebimentos;
  return (
    <div className={styles.stack}>
      <div>
        <progress
          aria-label="Progresso do fechamento"
          value={status.concluidos}
          max={status.total}
          style={{ width: '100%' }}
        />
        <p className="muted">
          {status.concluidos} de {status.total} passos
        </p>
      </div>

      <ol className={styles.list}>
        {status.passos.map((p) => (
          <PassoItem key={p.id} passo={p} />
        ))}
      </ol>

      {total === 0 ? (
        <p className="muted">Gere as faturas para acompanhar os recebimentos.</p>
      ) : (
        <div className={styles.row}>
          <p>
            Recebimentos: {pagaram} de {total} pagaram
          </p>
          {pagaram < total ? (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => navigate(`/recebimentos?competencia=${competenciaId}`)}
            >
              Ver quem falta
            </Button>
          ) : null}
        </div>
      )}
    </div>
  );
}

function PassoItem({ passo }: { passo: Passo }) {
  const { rotulo, rota } = PASSOS[passo.id] ?? { rotulo: passo.id };
  const estado = !passo.aplicavel
    ? 'Não se aplica'
    : passo.concluido
      ? 'Concluído'
      : passo.bloqueado
        ? 'Bloqueado'
        : 'Pendente';
  const tone = passo.concluido ? 'success' : undefined;
  return (
    <li className={styles.listItem} data-passo={passo.id}>
      <span style={{ display: 'flex', gap: 'var(--space-3)', flex: 1 }}>
        <span>{passo.numero}</span>
        <span>{rotulo}</span>
      </span>
      <Badge tone={tone}>{estado}</Badge>
      {!passo.aplicavel ? null : passo.bloqueado ? (
        <span className="muted">Conclua os passos 1 a 4 para publicar.</span>
      ) : rota ? (
        <Button
          size="sm"
          variant="ghost"
          aria-label={`Abrir passo ${passo.numero}: ${rotulo}`}
          onClick={() => navigate(rota)}
        >
          Abrir
        </Button>
      ) : (
        <span className="muted">Tela em breve</span>
      )}
    </li>
  );
}

/** Abertura de competência: mês/ano, vencimento e % do fundo de reserva. */
function AberturaForm({ onAberta }: { onAberta: () => Promise<void> }) {
  const hoje = new Date();
  const [mes, setMes] = useState(String(hoje.getMonth() + 1));
  const [ano, setAno] = useState(String(hoje.getFullYear()));
  const [vencimento, setVencimento] = useState('');
  const [reserva, setReserva] = useState('20');
  const [erros, setErros] = useState<Partial<Record<Campo, string>>>({});
  const [aviso, setAviso] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  async function enviar(e: FormEvent) {
    e.preventDefault();
    setAviso(null);
    // Campo vazio viraria Number('') = 0 e a API aceitaria 0% de reserva; a competência
    // não tem edição nem exclusão, então a validação precisa ser antes do envio.
    const locais: Partial<Record<Campo, string>> = {};
    if (!vencimento) locais.vencimento = ERRO_DE_CAMPO.invalid_vencimento?.mensagem;
    if (ano.trim() === '') locais.ano = ERRO_DE_CAMPO.invalid_ano?.mensagem;
    if (reserva.trim() === '') locais.reserva = ERRO_DE_CAMPO.invalid_reserva?.mensagem;
    if (Object.keys(locais).length > 0) {
      setErros(locais);
      return;
    }
    setErros({});
    setEnviando(true);
    const res = await mutateJson<{ error?: string }>('/api/competencias', {
      ano: Number(ano),
      mes: Number(mes),
      vencimento,
      pctFundoReserva: Number(reserva),
    });
    setEnviando(false);

    if (res.ok) {
      await onAberta();
      return;
    }
    const codigo = res.data?.error;
    const deCampo = codigo ? ERRO_DE_CAMPO[codigo] : undefined;
    if (deCampo) setErros({ [deCampo.campo]: deCampo.mensagem });
    else setAviso((codigo && ERRO_GERAL[codigo]) || ERRO_GERAL_PADRAO);
  }

  return (
    <Card title="Abrir competência">
      <form onSubmit={enviar} noValidate>
        {aviso ? <Alert kind="danger">{aviso}</Alert> : null}
        <div className={styles.row}>
          <Field label="Mês" error={erros.mes}>
            {(id) => (
              <Select id={id} value={mes} onChange={(e) => setMes(e.target.value)}>
                {MESES.map((nome, i) => (
                  <option key={nome} value={i + 1}>
                    {nome}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Ano" error={erros.ano}>
            {(id) => (
              <Input
                id={id}
                type="number"
                value={ano}
                onChange={(e) => setAno(e.target.value)}
                style={{ maxWidth: '8rem' }}
              />
            )}
          </Field>
          <Field label="Vencimento" error={erros.vencimento}>
            {(id) => (
              <Input
                id={id}
                type="date"
                value={vencimento}
                onChange={(e) => setVencimento(e.target.value)}
              />
            )}
          </Field>
          <Field label="Fundo de reserva (%)" error={erros.reserva}>
            {(id) => (
              <Input
                id={id}
                type="number"
                inputMode="numeric"
                value={reserva}
                onChange={(e) => setReserva(e.target.value)}
                style={{ maxWidth: '8rem' }}
              />
            )}
          </Field>
        </div>
        <Button type="submit" disabled={enviando}>
          Abrir competência
        </Button>
      </form>
    </Card>
  );
}
